// hooks/useAssistInput.tsx
// アシスト入力（apto enquete）モーダルの共有ロジック。
//
// AssistInputModal は「ガワ」だけのコンポーネントで、実際の質問描画
// （#aptoenqform への描画 / window.SelectEnqueteInputAidModal 等の初期化）は
// 呼び出し側が担う必要がある。その初期化〜挿入コールバックまでを一箇所に集約し、
// FollowFormModal / AdditionalInfoSection など複数の利用箇所で共有する。

import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { AssistInputState, SelectEnqueteInputAidModal } from '../services/assist-input';
import { additionalAPI } from '../services/additional-api';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { AssistTextOption } from '../components/AssistInputModal';

export interface UseAssistInputOptions {
  /** getAssistTextList に渡す種別（フォロー: 'followContent'。顧客備考は省略） */
  listKind?: string;
  /** SelectEnqueteInputAidModal.kind（例: 'customerRemarks' / 'followContent'） */
  kind: string;
  /** AssistInputModal が描画する hidden input の id（コンテンツ取得時にも参照） */
  hiddenInputId: string;
  /** デフォルトアシストNoを取り出す user オブジェクトのフィールド名
   *  （例: 'defaultcustomerassistno' / 'defaultfollowassistno'） */
  defaultUserField: string;
  /** 選択確定時に呼ばれる挿入処理（テキストを呼び出し側のフォーム等へ反映） */
  onInsert: (text: string) => void;
  /** モーダルを開いた直後に実行する追加処理（モバイルキーボード対応など） */
  onAssistOpen?: () => void;
  /** モーダル表示中に body へ付与するクラス（BCD系レイアウトCSSの制御用。省略可） */
  bodyClassOnOpen?: string;
}

export interface AssistModalSharedProps {
  open: boolean;
  onClose: () => void;
  options: AssistTextOption[];
  selectedValue: number | null;
  onValueChange: (value: number) => void;
  contentHtml: string;
  loading: boolean;
  error: string;
  errorRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  optionsError: boolean;
  hiddenInputId: string;
}

export interface UseAssistInputReturn {
  assistOptions: AssistTextOption[];
  assistOptionsError: boolean;
  defaultAssistNo: number | null;
  assistModalVisible: boolean;
  setAssistModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAssistEnq: number | null;
  setSelectedAssistEnq: React.Dispatch<React.SetStateAction<number | null>>;
  assistModalError: string;
  setAssistModalError: React.Dispatch<React.SetStateAction<string>>;
  assistContentHtml: string;
  loadingAssistContent: boolean;
  assistModalErrorRef: React.RefObject<HTMLDivElement | null>;
  assistContentRef: React.RefObject<HTMLDivElement | null>;
  /** アシスト入力ボタンの onClick に割り当てる */
  onAssistClick: () => void;
  /** AssistInputModal へ spread する共通 props（layout/width 等は呼び出し側で付与） */
  assistModalProps: AssistModalSharedProps;
}

export const useAssistInput = (options: UseAssistInputOptions): UseAssistInputReturn => {
  const { listKind, kind, hiddenInputId, defaultUserField, onInsert, onAssistOpen, bodyClassOnOpen } =
    options;

  const [assistOptions, setAssistOptions] = useState<AssistTextOption[]>([]);
  const [assistOptionsError, setAssistOptionsError] = useState<boolean>(false);
  const [defaultAssistNo, setDefaultAssistNo] = useState<number | null>(null);
  const [assistModalVisible, setAssistModalVisible] = useState<boolean>(false);
  const [selectedAssistEnq, setSelectedAssistEnq] = useState<number | null>(null);
  const [assistModalError, setAssistModalError] = useState<string>('');
  const [assistContentHtml, setAssistContentHtml] = useState<string>('');
  const [loadingAssistContent, setLoadingAssistContent] = useState<boolean>(false);

  const assistModalErrorRef = useRef<HTMLDivElement>(null);
  const assistContentRef = useRef<HTMLDivElement>(null);
  const assistOptionsRef = useRef<AssistTextOption[]>([]);
  // stale closure 回避のため最新の onInsert を ref で保持
  const onInsertRef = useRef(onInsert);

  useEffect(() => {
    onInsertRef.current = onInsert;
  }, [onInsert]);

  useEffect(() => {
    assistOptionsRef.current = assistOptions;
  }, [assistOptions]);

  // モーダル表示中に body クラスをトグル（指定時のみ）
  useEffect(() => {
    if (!bodyClassOnOpen) return;
    if (assistModalVisible) {
      document.body.classList.add(bodyClassOnOpen);
    } else {
      document.body.classList.remove(bodyClassOnOpen);
    }
    return () => {
      document.body.classList.remove(bodyClassOnOpen);
    };
  }, [assistModalVisible, bodyClassOnOpen]);

  // ----- アシストテキスト一覧取得（マウント時）＋デフォルトアシストNo解決 -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAssistOptionsError(false);
      try {
        const result = listKind
          ? await additionalAPI.getAssistTextList(listKind)
          : await additionalAPI.getAssistTextList();
        if (cancelled) return;
        if (result.success && result.data && Array.isArray(result.data)) {
          const opts: AssistTextOption[] =
            result.data.length > 0 && typeof result.data[0] === 'string'
              ? (result.data as string[]).map((text, i) => ({ enqNo: i + 1, enqName: text }))
              : (result.data as AssistTextOption[]);
          setAssistOptions(opts);

          // デフォルトアシストNoは一覧に実在する場合のみ採用（センチネル値 -999 等を弾く）
          try {
            const userStr = await indexedDBStorage.getItem('user');
            if (!cancelled && userStr) {
              const user = JSON.parse(userStr);
              const defaultNo: number | null = user?.[defaultUserField] ?? null;
              const isValid =
                defaultNo !== null && opts.some((o) => o.enqNo === defaultNo);
              setDefaultAssistNo(isValid ? defaultNo : null);
            }
          } catch {
            /* デフォルト解決失敗は無視（null のまま） */
          }
        } else {
          setAssistOptions([]);
          setAssistOptionsError(true);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('アシスト一覧取得エラー:', e);
          setAssistOptions([]);
          setAssistOptionsError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKind, defaultUserField]);

  // ----- 選択アシスト変更時にコンテンツ取得＆フォーム初期化 -----
  useEffect(() => {
    if (selectedAssistEnq === null || !assistModalVisible) return;

    let cancelled = false;
    (async () => {
      setLoadingAssistContent(true);
      try {
        const hiddenInput = document.getElementById(hiddenInputId) as HTMLInputElement | null;
        if (hiddenInput) hiddenInput.value = selectedAssistEnq.toString();

        const result = await additionalAPI.getAssistText(selectedAssistEnq);
        if (cancelled) return;
        if (!result.success || !result.data) {
          setAssistModalError('アシストテキストの取得に失敗しました。');
          return;
        }

        let parsedObj: any;
        try {
          parsedObj = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
        } catch {
          setAssistModalError('アシストテキストの取得に失敗しました。');
          return;
        }

        if (parsedObj.statuscode && parsedObj.statuscode !== 200) {
          setAssistModalError('アシストテキストの取得に失敗しました。');
          return;
        }

        const modalObj = (window as any).SelectEnqueteInputAidModal;
        if (modalObj) modalObj.enquete = parsedObj;

        // 質問リストの抽出（新旧フォーマット対応）
        let questionList: any[] = [];
        if (parsedObj.questionlist) {
          questionList = Array.isArray(parsedObj.questionlist)
            ? parsedObj.questionlist
            : (() => {
                try {
                  return typeof parsedObj.questionlist === 'string'
                    ? JSON.parse(parsedObj.questionlist)
                    : [];
                } catch {
                  return [];
                }
              })();
        } else if (parsedObj.questionInfoList && Array.isArray(parsedObj.questionInfoList)) {
          questionList = parsedObj.questionInfoList;
        }

        const css = parsedObj.css || '';
        const overrideCss = `<style>
  .aptoenq_qa { margin: 0 !important; }
  .whitespace { white-space: pre-wrap; overflow-wrap: break-word; }
  .aptoenq_addtext { max-width: 100%; width: 100%; box-sizing: border-box; }
  textarea { font-family: inherit; font-size: inherit; padding: 5px; border: 1px solid #d9d9d9; border-radius: 4px; width: 100%; max-width: 100%; box-sizing: border-box; }
  label { display: block; margin-bottom: 10px; width: 100%; }
  .aptoenq_a { width: 100%; overflow-x: hidden; }
  .assist-content { width: 100%; overflow-x: hidden; }
  .assist-content * { max-width: 100%; }
</style>`;
        setAssistContentHtml(`<style>${typeof css === 'string' ? css : ''}</style>${overrideCss}`);

        (window as any).enqQuestions = questionList;
        (window as any).enqCss = css;
        window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
        window.State = AssistInputState;

        (window as any).showAssistErrorInModal = (errorText: string) => {
          setAssistModalError(errorText);
          setTimeout(() => {
            assistModalErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        };

        SelectEnqueteInputAidModal.enquete = parsedObj;
        SelectEnqueteInputAidModal.kind = kind;

        const selectedOpt = assistOptionsRef.current.find((o) => o.enqNo === selectedAssistEnq);
        if (selectedOpt) SelectEnqueteInputAidModal.selectedEnqName = selectedOpt.enqName;

        SelectEnqueteInputAidModal.callback = (text: string) => {
          onInsertRef.current(text);
          setAssistModalVisible(false);
        };

        const aptoenqform = document.getElementById('aptoenqform');
        if (aptoenqform) aptoenqform.innerHTML = '';

        setTimeout(() => {
          initializeAssistForm();
        }, 300);
      } catch (e) {
        if (!cancelled) {
          console.error('アシストテキスト取得エラー:', e);
          setAssistModalError('アシストテキストの取得に失敗しました。');
        }
      } finally {
        if (!cancelled) setLoadingAssistContent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssistEnq, assistModalVisible]);

  // ----- アシストフォーム初期化（#aptoenqform へ質問を描画） -----
  const initializeAssistForm = () => {
    try {
      if (!(window as any).enqQuestions) return;

      let aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        const container = document.querySelector('.ant-modal-body [style*="border"]');
        if (container) {
          const existing = container.querySelector('#aptoenqform');
          if (existing) {
            aptoenqform = existing as HTMLElement;
          } else {
            aptoenqform = document.createElement('div');
            aptoenqform.id = 'aptoenqform';
            aptoenqform.className = 'fontM';
            container.innerHTML = '';
            container.appendChild(aptoenqform);
          }
        } else {
          message.error('フォーム表示エリアが見つかりません。再度試してください。');
          return;
        }
      }

      if (aptoenqform) aptoenqform.innerHTML = '';

      let processedQuestions: any[] = [];
      const raw = (window as any).enqQuestions;
      if (Array.isArray(raw)) {
        processedQuestions = raw;
      } else if (typeof raw === 'string') {
        try {
          processedQuestions = JSON.parse(raw);
        } catch {
          /* noop */
        }
      } else if (raw && typeof raw === 'object') {
        const vals = Object.values(raw);
        if (vals.length > 0) processedQuestions = vals as any[];
      }

      if (processedQuestions.length === 0) {
        message.error('質問が見つかりません');
        return;
      }

      let cssData: any[] = [];
      const rawCss = (window as any).enqCss;
      if (typeof rawCss === 'string' && rawCss.trim()) {
        try {
          const rules = rawCss.match(/([^{]+)\s*{\s*([^}]+)\s*}/g) || [];
          cssData = rules.map((rule: string) => {
            const m = rule.match(/([^{]+)\s*{\s*([^}]+)\s*}/) || [];
            return [m[1]?.trim(), m[2]?.trim()];
          });
        } catch {
          /* noop */
        }
      } else if (Array.isArray(rawCss)) {
        cssData = rawCss;
      }

      aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォーム表示要素の準備に失敗しました');
        return;
      }

      if (!Array.isArray(processedQuestions)) {
        message.error('質問データが正しい形式ではありません');
        return;
      }

      const state = new AssistInputState(processedQuestions, cssData);
      (window as any).State = AssistInputState;
      state.reset();

      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) resetBtn.removeAttribute('disabled');
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) selectBtn.removeAttribute('disabled');
    } catch (e) {
      console.error('アシストフォーム初期化エラー:', e);
      message.error('アシストフォームの初期化に失敗しました');
    }
  };

  // ----- アシスト入力ボタン onClick -----
  const onAssistClick = () => {
    window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
    window.State = AssistInputState;

    (window as any).showAssistErrorInModal = (errorText: string) => {
      setAssistModalError(errorText);
      setTimeout(() => {
        assistModalErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    };

    SelectEnqueteInputAidModal.callback = (text: string) => {
      onInsertRef.current(text);
      setAssistModalVisible(false);
    };
    SelectEnqueteInputAidModal.kind = kind;

    setSelectedAssistEnq(defaultAssistNo);
    setAssistModalError('');
    setAssistModalVisible(true);

    onAssistOpen?.();
  };

  const assistModalProps: AssistModalSharedProps = {
    open: assistModalVisible,
    onClose: () => {
      setAssistModalVisible(false);
      setSelectedAssistEnq(null);
    },
    options: assistOptions,
    selectedValue: selectedAssistEnq,
    onValueChange: (val: number) => setSelectedAssistEnq(val),
    contentHtml: assistContentHtml,
    loading: loadingAssistContent,
    error: assistModalError,
    errorRef: assistModalErrorRef,
    contentRef: assistContentRef,
    optionsError: assistOptionsError,
    hiddenInputId,
  };

  return {
    assistOptions,
    assistOptionsError,
    defaultAssistNo,
    assistModalVisible,
    setAssistModalVisible,
    selectedAssistEnq,
    setSelectedAssistEnq,
    assistModalError,
    setAssistModalError,
    assistContentHtml,
    loadingAssistContent,
    assistModalErrorRef,
    assistContentRef,
    onAssistClick,
    assistModalProps,
  };
};
